import { createContext, type MutableRefObject, type ReactElement, type ReactNode, useContext, useMemo } from 'react';
import invariant from 'tiny-invariant';
import { toast } from 'react-toastify';
import { useRealtimeEvent, useRealtimeParticipants } from 'services';
import { PresenceUsersProvider } from 'components/PresenceUsers';
import type { PlanDiffResponse } from '../plan/diff';
import { blockInvalidate, planPatch } from './realtimeDefinitions';
import { createBlockRealtimeCoordinator } from './realtimeCoordinator';

type BlockRealtimeContextValue = {
  channelKey: string;
  registerPlanPatchHandler(handler: (data: PlanDiffResponse) => void): () => void;
};

const BlockRealtimeContext = createContext<BlockRealtimeContextValue | null>(null);

export function useBlockRealtime(): BlockRealtimeContextValue {
  const context = useContext(BlockRealtimeContext);
  invariant(context, 'useBlockRealtime must be used within a BlockRealtimeProvider');
  return context;
}

export function BlockRealtimeProvider({
  blockId,
  revisionRef,
  invalidateBlock,
  children,
}: {
  blockId: number;
  revisionRef: MutableRefObject<number | undefined>;
  invalidateBlock: () => void;
  children: ReactNode;
}): ReactElement {
  const channelKey = `campaign:block:${blockId}`;
  const coordinator = useMemo(
    () =>
      createBlockRealtimeCoordinator({
        revisionRef,
        invalidateBlock,
        notifyConcurrentUpdate: () => toast.info('Concurrent update'),
        notifyResync: () => toast.info('Resyncing concurrent update'),
      }),
    [invalidateBlock, revisionRef],
  );

  useRealtimeEvent(channelKey, blockInvalidate, coordinator.handleBlockInvalidate);
  useRealtimeEvent(channelKey, planPatch, coordinator.handlePlanPatch);
  // The roster only needs identities; decoding the plan slice is `usePlanPresence`'s business.
  const participants = useRealtimeParticipants(channelKey);

  const value = useMemo<BlockRealtimeContextValue>(
    () => ({ channelKey, registerPlanPatchHandler: coordinator.registerPlanPatchHandler }),
    [channelKey, coordinator.registerPlanPatchHandler],
  );

  return (
    <BlockRealtimeContext.Provider value={value}>
      <PresenceUsersProvider participants={participants}>{children}</PresenceUsersProvider>
    </BlockRealtimeContext.Provider>
  );
}
